/**
 * Starter prompt the user pastes into Claude.ai / ChatGPT / Codex on a
 * freshly-set-up document.
 *
 * Voice and structure decisions (Phase 12.7 — UX revision):
 *
 *   1. **First-person.** The prompt is the user dictating to their own
 *      agent ("I've just set up a document"). Not a third-person narrator
 *      describing what's happening to a generic agent.
 *
 *   2. **No tool-call syntax in the visible text.** Modern agents
 *      introspect their MCP surface and pick tools themselves. We say
 *      "pull up the document" and let them call get_outline; we don't
 *      hand them `get_outline(session_id="…")` as if they couldn't read
 *      docs. Tool-call strings are debugger output, not human-facing copy.
 *
 *   3. **A URL, not a UUID.** Humans recognise URLs. The doc URL is
 *      enough — agents resolve it via discover_documents or by reading
 *      the path. The `session_id` only resurfaces in the troubleshooting
 *      footer below, where it belongs.
 *
 *   4. **Outcome-shaped steps, not procedural ones.** "Tell me what you
 *      think the outline should look like" gives the agent a concrete
 *      deliverable; "call get_outline" is a procedure with no clear stop.
 *
 *   5. **Kind-specific guidance is one sentence**, slotted into step 2.
 *      Five-line per-kind sub-recipes were too prescriptive and pushed the
 *      total length past skim-tolerance.
 *
 *   6. **Cross-doc discovery dropped from the default.** The first-paste
 *      moment should focus on the doc the user just created. Agents that
 *      want broader work can call `find_open_contributions` once the
 *      first task lands; that's a "give my agent more to do" prompt the
 *      user can copy later, not a fresh-onboarding step.
 *
 *   7. **Troubleshooting moved out of the prompt.** The
 *      "session_id_required" debugging note belongs in a "stuck?"
 *      expander on the doc page, not in the script the user pastes.
 */

import type { DocumentKind } from "@/lib/api";

export interface StarterPromptInput {
  sessionId: string;
  title: string;
  kind: DocumentKind;
  language?: string | null;
  /** Frontend host where the doc is browsable. Defaults to the prod SPA;
   *  passed in explicitly for testability and for self-hosted setups. */
  appOrigin?: string;
}

const DEFAULT_APP_ORIGIN = "https://app.coauthor.skarp.app";

/** One sentence about what the doc *is*, slotted into step 2. */
function kindFlavour(kind: DocumentKind, language?: string | null): string {
  switch (kind) {
    case "recipe": {
      const lang = language ? ` Use ${language}.` : "";
      return (
        `This is a code recipe — there are four sections scaffolded ` +
        `(Use case · Code · Explanation · Alternatives & edge cases).${lang}`
      );
    }
    case "qa":
      return (
        `This is a Q&A — write the canonical answer that you'd want to ` +
        `find in a search result. Cover the typical path, the gotchas, ` +
        `and any common alternatives.`
      );
    case "reference":
      return (
        `This is a reference doc — propose a structure first (categories, ` +
        `lookup tables, cross-links), then fill it in with concise content.`
      );
    case "style_guide":
      return (
        `This is a style guide — write prescriptive rules with brief ` +
        `rationales and short examples. People will reference this when ` +
        `reviewing each other's work.`
      );
    case "general":
    default:
      return ``;
  }
}

export function buildStarterPrompt(input: StarterPromptInput): string {
  const { sessionId, title, kind, language } = input;
  const origin = (input.appOrigin ?? DEFAULT_APP_ORIGIN).replace(/\/$/, "");
  const url = `${origin}/d/${sessionId}`;
  const flavour = kindFlavour(kind, language);

  // Step 2 picks up the kind-specific sentence inline so we don't end up
  // with an awkward bullet sandwich.
  const step2 = flavour
    ? `2. Once we agree on the outline, claim the first section that needs ` +
      `work and draft it. ${flavour} Leave a comment on the section when ` +
      `you're done so I can review.`
    : `2. Once we agree on the outline, claim the first section that needs ` +
      `work and draft it. Leave a comment on the section when you're done ` +
      `so I can review.`;

  return [
    `I've just set up a Coauthor document for us to work on — "${title}":`,
    `  ${url}`,
    ``,
    `Please:`,
    ``,
    `1. Pull up the document and any source materials I've attached, then ` +
      `tell me what you think the outline should look like before drafting.`,
    step2,
    `3. Repeat down the outline. If you have questions, drop them as ` +
      `comments on the relevant section — I'll see them.`,
  ].join("\n");
}
